import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import type { LocationFormValues } from "@/features/locations/schema";
import type { StockLocation, StockLocationListRow } from "@/features/locations/types";

const text = (value: string | undefined) => value?.trim() || null;

export function useStockLocations() {
  return useQuery({
    queryKey: ["stock_locations"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<StockLocationListRow[]> => {
      const { data, error } = await supabase
        .from("stock_locations")
        .select("*, parent:stock_locations!parent_id(id, code, name)")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as StockLocationListRow[];
    },
  });
}

export function useUpsertStockLocation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id?: string;
      values: LocationFormValues;
    }) => {
      if (id && values.parent_id === id) {
        throw new Error("A location cannot be its own parent");
      }

      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        location_type: values.location_type,
        parent_id: values.parent_id?.trim() || null,
        description: text(values.description),
        is_active: values.is_active,
        sort_order: values.sort_order,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      if (id) {
        const { data, error } = await supabase
          .from("stock_locations")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return data as StockLocation;
      }

      const { data, error } = await supabase
        .from("stock_locations")
        .insert({
          ...payload,
          created_by: user?.id ?? null,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as StockLocation;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stock_locations"] });
      toast.success("Location saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save location"),
  });
}

export function useDeleteStockLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("stock_locations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stock_locations"] });
      toast.success("Location deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete location"),
  });
}
